import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Package, Building2, Wrench, Calendar, Printer, CheckCircle, Clock, AlertTriangle, Truck, Mail, FileText, Eye, Lock, Users, DollarSign, Settings as SettingsIcon, Upload, Image, X, Pencil, KeyRound, Copy, RefreshCw } from 'lucide-react';

function Settings() {
  const navigate = useNavigate();
  const { user, token, API, companyContext } = useContext(AuthContext);
  const [sundries, setSundries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [poTemplates, setPoTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [branchEditMode, setBranchEditMode] = useState(false);
  const [supplierEditMode, setSupplierEditMode] = useState(false);
  const [templateEditMode, setTemplateEditMode] = useState(false);
  const [currentSundry, setCurrentSundry] = useState(null);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  
  // CorelDRAW Upload settings
  const [corelSettings, setCorelSettings] = useState(null);
  const [corelLoading, setCorelLoading] = useState(false);
  const [corelKeyVisible, setCorelKeyVisible] = useState(false);
  
  // Check if user has worktops feature enabled
  const hasWorktopsFeature = companyContext?.features?.worktops_enabled !== false;
  const hasMaintenanceFeature = companyContext?.features?.maintenance_enabled !== false; // Available on upper tiers for both memorial and worktop customers
  // Check if user has memorials feature enabled
  const hasMemorialsFeature = companyContext?.features?.memorials_enabled !== false;
  // Check if demo user (demo users shouldn't see materials/worktop settings)
  const isDemoUser = user?.role === 'demo' || user?.is_demo;
  
  const [formData, setFormData] = useState({
    description: '',
    price: '',
    is_active: true
  });
  const [branchFormData, setBranchFormData] = useState({
    name: '',
    short_code: '',
    address: '',
    phone: '',
    email: '',
    is_active: true
  });
  const [supplierFormData, setSupplierFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    contact_name: '',
    notes: ''
  });
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    supplier_id: '',
    supplier_name: '',
    supplier_email: '',
    description: '',
    items: [{ description: '', quantity: 1 }],
    is_consumable: false
  });
  const [letteringRates, setLetteringRates] = useState({
    gold_leaf: 2.50,
    gold_paint: 1.50,
    silver: 1.50,
    black: 1.00,
    white: 1.00,
    colour: 1.50,
    hand_cut_and_gild: 3.00,
    hand_cut: 1.75,
    machine_cut: 1.25,
    lead: 1.50,
    raised_lead: 2.00
  });
  const [newLetteringType, setNewLetteringType] = useState({ name: '', rate: '' });
  const [freeLetters, setFreeLetters] = useState(150);
  const [worktopSettings, setWorktopSettings] = useState({
    retail_multiplier: 3.1,
    templating_cost: 150,
    fitting_cost: 450,
    undermount_sink_cost: 50,
    overmount_sink_cost: 30,
    hob_cutout_cost: 40,
    socket_cutout_cost: 25,
    popup_socket_cost: 35,
    tap_hole_cost: 20,
    radius_corner_cost: 30,
    large_rounded_corner_cost: 50,
    drainer_grooves_cost: 40,
    recessed_drainer_cost: 60,
    custom_cutout_cost_per_m: 25,
    delivery_rate_per_mile: 1.50,
    edge_profiles: {
      'Pencil Round': 15,
      'Bevel': 18,
      'Book End': 25,
      'Bullnose': 22,
      'Ogee': 28,
      'Half Bullnose': 20
    },
    mitred_edge_cost: 45,  // Cost per meter for mitred edges
    face_polish_cost: 0,   // Cost per m² for face polishing
    custom_extras: []  // Array of {id, name, cost, requiresDimensions}
  });
  const [newEdgeProfile, setNewEdgeProfile] = useState({ name: '', cost: '' });
  const [editingEdge, setEditingEdge] = useState(null);
  const [newCustomExtra, setNewCustomExtra] = useState({ name: '', cost: '', requiresDimensions: false });
  const [editingExtra, setEditingExtra] = useState(null);
  
  // Maintenance schedules state
  const [maintenanceSchedules, setMaintenanceSchedules] = useState([]);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [maintenanceEditMode, setMaintenanceEditMode] = useState(false);
  const [currentMaintenance, setCurrentMaintenance] = useState(null);
  const [maintenanceFormData, setMaintenanceFormData] = useState({
    machine_name: '',
    description: '',
    frequency: 'monthly',
    checklist: [],
    next_due_date: ''
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');
  
  // Task Automation state
  const [taskAssignees, setTaskAssignees] = useState({});
  const [companyUsers, setCompanyUsers] = useState([]);
  const [taskAssigneesLoading, setTaskAssigneesLoading] = useState(false);

  // Email Templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
  const [canCustomizeTemplates, setCanCustomizeTemplates] = useState(false);
  const [templateVariables, setTemplateVariables] = useState({});
  const [emailTemplateDialogOpen, setEmailTemplateDialogOpen] = useState(false);
  const [currentEmailTemplate, setCurrentEmailTemplate] = useState(null);
  const [emailTemplateForm, setEmailTemplateForm] = useState({
    template_type: '',
    subject: '',
    body: '',
    status_trigger: ''
  });
  const [companyBranding, setCompanyBranding] = useState({
    logo_url: '',
    logo_data: '',
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    tagline: ''
  });
  const [emailLogoOption, setEmailLogoOption] = useState('company'); // 'company', 'custom', 'none'
  const [customEmailLogo, setCustomEmailLogo] = useState('');
  const [customEmailLogoPreview, setCustomEmailLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoFileInputRef = React.useRef(null);

  // Order number prefix (e.g. "SIS" so orders become SIS26001)
  const [orderNumberPrefix, setOrderNumberPrefix] = useState('');
  const [savedOrderNumberPrefix, setSavedOrderNumberPrefix] = useState('');
  const [savingOrderPrefix, setSavingOrderPrefix] = useState(false);

  // Active settings tab
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchSundries();
    fetchBranches();
    fetchSuppliers();
    fetchPoTemplates();
    fetchLetteringRates();
    fetchWorktopSettings();
    fetchTaskAssignees();
    fetchCompanyUsers();
    fetchEmailTemplates();
    fetchCompanyBranding();
    fetchMaintenanceSchedules(); // Now available for all upper-tier customers
    fetchCorelSettings();
  }, []);

  const fetchCorelSettings = async () => {
    try {
      const response = await axios.get(`${API}/coreldraw/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorelSettings(response.data);
    } catch (e) {
      setCorelSettings(null);
    }
  };

  const regenerateCorelKey = async () => {
    if (corelSettings?.has_key && !window.confirm('Generate a NEW CorelDRAW API key? The old key will stop working immediately.')) return;
    setCorelLoading(true);
    try {
      const response = await axios.post(`${API}/coreldraw/regenerate-key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorelSettings({ ...corelSettings, ...response.data, has_key: true });
      setCorelKeyVisible(true);
      toast.success('CorelDRAW API key generated. Copy it now — you can regenerate at any time.');
    } catch (e) {
      toast.error('Failed to generate API key');
    } finally {
      setCorelLoading(false);
    }
  };

  const copyCorelValue = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => toast.success(`${label} copied to clipboard`)).catch(() => toast.error('Copy failed'));
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchPoTemplates = async () => {
    try {
      const response = await axios.get(`${API}/po-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPoTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch PO templates:', error);
    }
  };

  const handleTemplateSubmit = async () => {
    try {
      // Filter out empty items
      const cleanedItems = templateFormData.items.filter(item => item.description.trim() !== '');
      const dataToSend = { ...templateFormData, items: cleanedItems };
      
      if (templateEditMode && currentTemplate) {
        await axios.put(`${API}/po-templates/${currentTemplate.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/po-templates`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template saved');
      }
      setTemplateDialogOpen(false);
      setTemplateFormData({ name: '', supplier_id: '', supplier_name: '', supplier_email: '', description: '', items: [{ description: '', quantity: 1 }], is_consumable: false });
      setTemplateEditMode(false);
      setCurrentTemplate(null);
      fetchPoTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/po-templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchPoTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Task Assignees functions
  const fetchTaskAssignees = async () => {
    if (!user?.company_id) return;
    try {
      const response = await axios.get(`${API}/companies/${user.company_id}/task-assignees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskAssignees(response.data.task_assignees || {});
    } catch (error) {
      console.error('Failed to fetch task assignees:', error);
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show admin/staff users who can be assigned tasks
      setCompanyUsers(response.data.filter(u => ['admin', 'super_admin', 'company_admin', 'staff'].includes(u.role)));
    } catch (error) {
      console.error('Failed to fetch company users:', error);
    }
  };

  const handleTaskAssigneeChange = (trigger, userEmail) => {
    setTaskAssignees(prev => ({
      ...prev,
      [trigger]: userEmail || undefined
    }));
  };

  const saveTaskAssignees = async () => {
    if (!user?.company_id) return;
    setTaskAssigneesLoading(true);
    try {
      // Clean up empty values
      const cleanedAssignees = Object.fromEntries(
        Object.entries(taskAssignees).filter(([_, v]) => v && v.trim())
      );
      
      await axios.put(`${API}/companies/${user.company_id}/task-assignees`, 
        { task_assignees: cleanedAssignees },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task automation settings saved');
    } catch (error) {
      toast.error('Failed to save task automation settings');
    } finally {
      setTaskAssigneesLoading(false);
    }
  };

  // Email Template functions
  const fetchEmailTemplates = async () => {
    setEmailTemplatesLoading(true);
    try {
      const response = await axios.get(`${API}/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data.templates || []);
      setCanCustomizeTemplates(response.data.can_customize || false);
      setTemplateVariables(response.data.variables || {});
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    } finally {
      setEmailTemplatesLoading(false);
    }
  };

  const openEmailTemplateEditor = (template) => {
    setCurrentEmailTemplate(template);
    setEmailTemplateForm({
      template_type: template.template_type,
      subject: template.subject,
      body: template.body,
      status_trigger: template.status_trigger || ''
    });
    setEmailTemplateDialogOpen(true);
  };

  const saveEmailTemplate = async () => {
    try {
      await axios.post(`${API}/email-templates`, emailTemplateForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email template saved');
      setEmailTemplateDialogOpen(false);
      fetchEmailTemplates();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save template'); toast.error(msg);
    }
  };

  const resetEmailTemplate = async (templateId) => {
    if (!window.confirm('Reset this template to the default? Your customizations will be lost.')) return;
    try {
      await axios.delete(`${API}/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template reset to default');
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const getTemplateTypeLabel = (type) => {
    const labels = {
      quote: 'Quote Email',
      invoice: 'Invoice Email',
      artwork: 'Artwork Approval',
      artwork_changes: 'Artwork Changes',
      status_change: 'Status Update',
      completion: 'Order Completion',
      contact_client: 'Contact Client'
    };
    return labels[type] || type;
  };

  const getTemplateTypeDescription = (type) => {
    const descriptions = {
      quote: 'Sent when a quote is generated for a customer',
      invoice: 'Sent when an invoice is created',
      artwork: 'Sent when artwork is ready for customer approval',
      artwork_changes: 'Sent when artwork has been updated after customer feedback',
      status_change: 'Sent when order status changes',
      completion: 'Sent when the memorial is complete',
      contact_client: 'Template for contacting clients with questions about their order'
    };
    return descriptions[type] || '';
  };

  const fetchCompanyBranding = async () => {
    try {
      const companyId = user?.company_id || 'set-in-stone';
      const response = await axios.get(`${API}/companies/${companyId}/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Also fetch company details for name, address, etc
      const companyRes = await axios.get(`${API}/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const branding = companyRes.data.branding || {};
      setCompanyBranding({
        logo_url: response.data.logo_url || branding.logo_url || '',
        logo_data: branding.logo_data || '',
        company_name: companyRes.data.name || '',
        company_address: companyRes.data.address || '',
        company_phone: companyRes.data.phone || '',
        company_email: companyRes.data.email || '',
        tagline: response.data.tagline || branding.tagline || ''
      });
      const prefix = (companyRes.data.order_number_prefix || '').toString();
      setOrderNumberPrefix(prefix);
      setSavedOrderNumberPrefix(prefix);
    } catch (error) {
      console.error('Failed to fetch company branding:', error);
    }
  };

  const saveOrderNumberPrefix = async () => {
    const cleaned = (orderNumberPrefix || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length > 6) {
      toast.error('Prefix should be at most 6 letters/digits');
      return;
    }
    setSavingOrderPrefix(true);
    try {
      const companyId = user?.company_id || 'set-in-stone';
      await axios.put(`${API}/companies/${companyId}`,
        { order_number_prefix: cleaned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrderNumberPrefix(cleaned);
      setSavedOrderNumberPrefix(cleaned);
      toast.success(cleaned ? `Order-number prefix saved: ${cleaned}` : 'Order-number prefix cleared');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save order-number prefix');
    } finally {
      setSavingOrderPrefix(false);
    }
  };

  // Handle logo file selection for email templates
  const handleLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomEmailLogoPreview(reader.result);
      setCustomEmailLogo(file);
    };
    reader.readAsDataURL(file);
  };

  // Upload custom logo to company branding
  const handleLogoUpload = async () => {
    if (!customEmailLogo) {
      toast.error('Please select a logo file first');
      return;
    }

    setUploadingLogo(true);
    try {
      const companyId = user?.company_id || 'set-in-stone';
      const formData = new FormData();
      formData.append('file', customEmailLogo);

      const response = await axios.post(`${API}/companies/${companyId}/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update local state with new logo
      setCompanyBranding(prev => ({
        ...prev,
        logo_data: response.data.logo_data,
        logo_url: response.data.logo_data
      }));
      
      setEmailLogoOption('company');
      setCustomEmailLogo('');
      setCustomEmailLogoPreview('');
      toast.success('Logo uploaded and saved to your company settings');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Get the current logo URL based on selection
  const getPreviewLogoUrl = () => {
    if (emailLogoOption === 'none') return null;
    if (emailLogoOption === 'custom' && customEmailLogoPreview) return customEmailLogoPreview;
    return companyBranding.logo_url || companyBranding.logo_data;
  };

  // Generate live preview HTML for email template
  const generateEmailPreview = () => {
    // Replace variables with sample data
    const sampleData = {
      client_name: 'John Smith',
      order_number: 'ORD-2024-001',
      total_amount: '1,250.00',
      invoice_amount: '1,250.00',
      amount_paid: '500.00',
      balance_due: '750.00',
      quote_date: new Date().toLocaleDateString('en-GB'),
      invoice_date: new Date().toLocaleDateString('en-GB'),
      memorial_type: 'Headstone',
      artwork_link: 'https://example.com/artwork',
      version_number: '2',
      change_notes: 'Updated inscription text',
      new_status: 'In Production',
      old_status: 'Approved',
      status_message: 'Your memorial is now being crafted by our skilled team.',
      completion_details: 'Ready for collection or delivery.',
      completion_date: new Date().toLocaleDateString('en-GB'),
      company_name: companyBranding.company_name || 'Your Company',
      company_phone: companyBranding.company_phone || '01onal 234567',
      company_email: companyBranding.company_email || 'info@company.com',
      bank_details: 'Sort Code: 12-34-56\nAccount: 12345678'
    };

    let subject = emailTemplateForm.subject;
    let body = emailTemplateForm.body;

    // Replace all variables
    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      subject = subject.replace(regex, value);
      body = body.replace(regex, value);
    });

    // Convert newlines to <br> for HTML display
    const htmlBody = body.replace(/\n/g, '<br>');

    return { subject, body: htmlBody };
  };


  const handleQuickOrder = async (template) => {
    if (!window.confirm(`Send this order to ${template.supplier_name || template.supplier_email}?`)) return;
    try {
      await axios.post(`${API}/po-templates/${template.id}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order sent to supplier');
    } catch (error) {
      toast.error('Failed to send order');
    }
  };

  const handleSupplierSubmit = async () => {
    try {
      if (supplierEditMode && currentSupplier) {
        await axios.put(`${API}/suppliers/${currentSupplier.id}`, supplierFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API}/suppliers`, supplierFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier added');
      }
      setSupplierDialogOpen(false);
      setSupplierFormData({ name: '', email: '', phone: '', address: '', contact_name: '', notes: '' });
      setSupplierEditMode(false);
      setCurrentSupplier(null);
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to save supplier');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const fetchLetteringRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/lettering-costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.costs) {
        setLetteringRates(response.data.costs);
      }
      if (response.data.free_characters !== undefined) {
        setFreeLetters(response.data.free_characters);
      }
    } catch (error) {
      console.error('Failed to fetch lettering rates');
    }
  };

  const addLetteringType = () => {
    const key = newLetteringType.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!key) { toast.error('Enter a name for the new lettering type'); return; }
    if (letteringRates[key] !== undefined) { toast.error('That lettering type already exists'); return; }
    setLetteringRates({ ...letteringRates, [key]: parseFloat(newLetteringType.rate) || 0 });
    setNewLetteringType({ name: '', rate: '' });
    toast.info('Added — click "Save Lettering Rates" to apply');
  };

  const removeLetteringType = (key) => {
    if (!window.confirm(`Remove "${key.replace(/_/g, ' ')}" from your lettering types?`)) return;
    const updated = { ...letteringRates };
    delete updated[key];
    setLetteringRates(updated);
    toast.info('Removed — click "Save Lettering Rates" to apply');
  };

  const saveLetteringRates = async () => {
    try {
      await axios.put(`${API}/settings/lettering-costs`, { costs: letteringRates, free_characters: parseInt(freeLetters) || 0 }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lettering rates updated successfully');
    } catch (error) {
      toast.error('Failed to update lettering rates');
    }
  };

  const fetchWorktopSettings = async () => {
    try {
      const response = await axios.get(`${API}/worktops/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorktopSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch worktop settings');
    }
  };

  const saveWorktopSettings = async () => {
    try {
      await axios.put(`${API}/worktops/settings`, worktopSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Worktop settings updated successfully');
    } catch (error) {
      toast.error('Failed to update worktop settings');
    }
  };

  const addEdgeProfile = () => {
    if (!newEdgeProfile.name || !newEdgeProfile.cost) {
      toast.error('Please enter profile name and cost');
      return;
    }
    
    setWorktopSettings({
      ...worktopSettings,
      edge_profiles: {
        ...worktopSettings.edge_profiles,
        [newEdgeProfile.name]: parseFloat(newEdgeProfile.cost)
      }
    });
    setNewEdgeProfile({ name: '', cost: '' });
    toast.success('Edge profile added');
  };

  const removeEdgeProfile = (profileName) => {
    if (!window.confirm(`Remove ${profileName} profile?`)) return;
    
    const { [profileName]: removed, ...rest } = worktopSettings.edge_profiles;
    setWorktopSettings({
      ...worktopSettings,
      edge_profiles: rest
    });
    toast.success('Edge profile removed');
  };

  const updateEdgeProfile = (oldName, newName, newCost) => {
    if (!newName || !newCost) {
      toast.error('Please enter profile name and cost');
      return;
    }

    const updatedProfiles = { ...worktopSettings.edge_profiles };
    
    // Remove old entry
    delete updatedProfiles[oldName];
    
    // Add new entry
    updatedProfiles[newName] = parseFloat(newCost);
    
    setWorktopSettings({
      ...worktopSettings,
      edge_profiles: updatedProfiles
    });
    
    setEditingEdge(null);
    toast.success('Edge profile updated');
  };

  const fetchSundries = async () => {
    try {
      const response = await axios.get(`${API}/sundries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSundries(response.data);
    } catch (error) {
      toast.error('Failed to fetch sundries');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentSundry(null);
    setFormData({ description: '', price: '', is_active: true });
    setDialogOpen(true);
  };

  const openEditDialog = (sundry) => {
    setEditMode(true);
    setCurrentSundry(sundry);
    setFormData({
      description: sundry.description,
      price: sundry.price.toString(),
      is_active: sundry.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        description: formData.description,
        price: parseFloat(formData.price),
        is_active: formData.is_active
      };

      if (editMode) {
        await axios.put(`${API}/sundries/${currentSundry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sundry updated successfully');
      } else {
        await axios.post(`${API}/sundries`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sundry created successfully');
      }

      setDialogOpen(false);
      fetchSundries();
    } catch (error) {
      toast.error(`Failed to ${editMode ? 'update' : 'create'} sundry`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sundry?')) {
      return;
    }

    try {
      await axios.delete(`${API}/sundries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sundry deleted successfully');
      fetchSundries();
    } catch (error) {
      toast.error('Failed to delete sundry');
    }
  };

  const toggleActive = async (sundry) => {
    try {
      await axios.put(`${API}/sundries/${sundry.id}`, 
        { is_active: !sundry.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sundry ${!sundry.is_active ? 'activated' : 'deactivated'}`);
      fetchSundries();
    } catch (error) {
      toast.error('Failed to update sundry status');
    }
  };

  // Branch functions
  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data?.branches || response.data || []);
    } catch (error) {
      toast.error('Failed to fetch branches');
    }
  };

  const openCreateBranchDialog = () => {
    setBranchEditMode(false);
    setCurrentBranch(null);
    setBranchFormData({ name: '', short_code: '', address: '', phone: '', email: '', is_active: true });
    setBranchDialogOpen(true);
  };

  const openEditBranchDialog = (branch) => {
    setBranchEditMode(true);
    setCurrentBranch(branch);
    setBranchFormData({
      name: branch.name,
      short_code: branch.short_code || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      is_active: branch.is_active
    });
    setBranchDialogOpen(true);
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();

    if (!branchFormData.name || !branchFormData.short_code) {
      toast.error('Branch name and short code are required');
      return;
    }

    try {
      const payload = {
        name: branchFormData.name,
        short_code: branchFormData.short_code.toUpperCase(),
        address: branchFormData.address || null,
        phone: branchFormData.phone || null,
        email: branchFormData.email || null,
        is_active: branchFormData.is_active
      };

      if (branchEditMode) {
        await axios.put(`${API}/branches/${currentBranch.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Branch updated successfully');
      } else {
        await axios.post(`${API}/branches`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Branch created successfully');
      }

      setBranchDialogOpen(false);
      fetchBranches();
    } catch (error) {
      toast.error(`Failed to ${branchEditMode ? 'update' : 'create'} branch`);
    }
  };

  const handleDeleteBranch = async (id) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) {
      return;
    }

    try {
      await axios.delete(`${API}/branches/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branch deleted successfully');
      fetchBranches();
    } catch (error) {
      toast.error('Failed to delete branch');
    }
  };

  // Maintenance Schedule Functions
  const fetchMaintenanceSchedules = async () => {
    try {
      const response = await axios.get(`${API}/maintenance-schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaintenanceSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch maintenance schedules');
    }
  };

  const openMaintenanceDialog = (schedule = null) => {
    if (schedule) {
      setMaintenanceEditMode(true);
      setCurrentMaintenance(schedule);
      setMaintenanceFormData({
        machine_name: schedule.machine_name || '',
        description: schedule.description || '',
        frequency: schedule.frequency || 'monthly',
        checklist: schedule.checklist || [],
        next_due_date: schedule.next_due_date || ''
      });
    } else {
      setMaintenanceEditMode(false);
      setCurrentMaintenance(null);
      setMaintenanceFormData({
        machine_name: '',
        description: '',
        frequency: 'monthly',
        checklist: [],
        next_due_date: ''
      });
    }
    setNewChecklistItem('');
    setMaintenanceDialogOpen(true);
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setMaintenanceFormData({
      ...maintenanceFormData,
      checklist: [...maintenanceFormData.checklist, newChecklistItem.trim()]
    });
    setNewChecklistItem('');
  };

  const removeChecklistItem = (index) => {
    setMaintenanceFormData({
      ...maintenanceFormData,
      checklist: maintenanceFormData.checklist.filter((_, i) => i !== index)
    });
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    if (!maintenanceFormData.machine_name.trim()) {
      toast.error('Please enter a machine name');
      return;
    }

    try {
      const payload = {
        machine_name: maintenanceFormData.machine_name,
        description: maintenanceFormData.description,
        frequency: maintenanceFormData.frequency,
        checklist: maintenanceFormData.checklist,
        next_due_date: maintenanceFormData.next_due_date || null
      };

      if (maintenanceEditMode && currentMaintenance) {
        await axios.put(`${API}/maintenance-schedules/${currentMaintenance.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Maintenance schedule updated');
      } else {
        await axios.post(`${API}/maintenance-schedules`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Maintenance schedule created');
      }

      setMaintenanceDialogOpen(false);
      fetchMaintenanceSchedules();
    } catch (error) {
      toast.error(`Failed to ${maintenanceEditMode ? 'update' : 'create'} maintenance schedule`);
    }
  };

  const deleteMaintenanceSchedule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this maintenance schedule?')) return;
    
    try {
      await axios.delete(`${API}/maintenance-schedules/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Maintenance schedule deleted');
      fetchMaintenanceSchedules();
    } catch (error) {
      toast.error('Failed to delete maintenance schedule');
    }
  };

  const printJobSheet = (scheduleId) => {
    window.open(`${API}/maintenance-schedules/${scheduleId}/job-sheet?token=${token}`, '_blank');
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      'weekly': 'Weekly',
      'bi-weekly': 'Every 2 Weeks',
      'monthly': 'Monthly',
      'bi-monthly': 'Every 2 Months',
      'yearly': 'Yearly'
    };
    return labels[freq] || freq;
  };

  const isDue = (schedule) => {
    if (!schedule.next_due_date) return false;
    const dueDate = new Date(schedule.next_due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate <= today;
  };

  if (!['admin', 'super_admin', 'company_admin', 'demo'].includes(user?.role)) {
    return <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto py-8">
        <p className="text-center text-slate-600">Access denied. Admin only.</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          </div>
        </div>

        {/* Tabbed Settings Interface */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full mb-6 bg-white shadow-sm rounded-lg p-1 ${hasWorktopsFeature ? 'grid-cols-6' : 'grid-cols-5'}`}>
            <TabsTrigger value="general" className="flex items-center gap-2 data-[state=active]:bg-slate-100">
              <SettingsIcon className="w-4 h-4" />
              <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2 data-[state=active]:bg-slate-100">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2 data-[state=active]:bg-slate-100">
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Products</span>
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="flex items-center gap-2 data-[state=active]:bg-slate-100">
              <Truck className="w-4 h-4" />
              <span className="hidden sm:inline">Suppliers</span>
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="flex items-center gap-2 data-[state=active]:bg-slate-100">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Maintenance</span>
            </TabsTrigger>
            {hasWorktopsFeature && (
              <TabsTrigger value="worktops" className="flex items-center gap-2 data-[state=active]:bg-slate-100">
                <Wrench className="w-4 h-4" />
                <span className="hidden sm:inline">Worktops</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* GENERAL TAB */}
          <TabsContent value="general" className="space-y-6">
            {/* Connect to Claude — master company (Set in Stone) only */}
            {(user?.role === 'super_admin' || user?.company_id === 'set-in-stone' || companyContext?.is_master === true) && (
            <Card className="border-0 shadow-xl border-l-4 border-l-orange-500" data-testid="claude-connector-link-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-xl">Connect to Claude</CardTitle>
                    <CardDescription>
                      Let your Claude app create and amend memorial & worktop orders in Memorial Desk on your command. Generate a secure key and follow the one-time setup.
                    </CardDescription>
                  </div>
                  <Button onClick={() => navigate('/settings/claude')} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="open-claude-connector">
                    Set up Claude →
                  </Button>
                </div>
              </CardHeader>
            </Card>
            )}

            {/* Terms & Customer Agreements — dedicated page */}
            <Card className="border-0 shadow-xl border-l-4 border-l-purple-500" data-testid="terms-agreements-link-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-xl">Terms & Customer Agreements</CardTitle>
                    <CardDescription>
                      Digital agreements customers sign before their order progresses. Master switch, per-type toggles, warranty numbers, editable templates with variables and required tick-boxes.
                    </CardDescription>
                  </div>
                  <Button onClick={() => navigate('/settings/agreements')} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="open-agreements-settings">
                    Open Agreements Settings →
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Application Form Templates — dedicated page */}
            <Card className="border-0 shadow-xl border-l-4 border-l-teal-500" data-testid="application-forms-link-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-xl">Application Form Templates</CardTitle>
                    <CardDescription>
                      Generate churchyard/cemetery applications on official authority forms. Includes the Oxford Diocese form built-in, plus upload any council, cemetery, diocese or church form and turn it into a reusable, auto-filled template.
                    </CardDescription>
                  </div>
                  <Button onClick={() => navigate('/settings/application-forms')} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="open-application-forms-settings">
                    Open Application Forms →
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Order Number Prefix */}
            <Card className="border-0 shadow-xl" data-testid="order-number-prefix-card">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-slate-800 to-black rounded-lg ring-1 ring-[#c9a24a]/40">
                    <span className="text-[#c9a24a] font-bold tracking-widest text-sm">#</span>
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Order Number Prefix</CardTitle>
                    <CardDescription>
                      A short company code that will be prepended to every new memorial order number
                      (e.g. <span className="font-mono text-slate-700">SIS</span> → <span className="font-mono">SIS26001</span>).
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-4 items-end">
                  <div>
                    <Label htmlFor="order-number-prefix" className="text-sm font-medium">Prefix</Label>
                    <Input
                      id="order-number-prefix"
                      value={orderNumberPrefix}
                      onChange={(e) => setOrderNumberPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                      placeholder="e.g. SIS"
                      className="mt-1 font-mono tracking-widest uppercase"
                      data-testid="order-number-prefix-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Letters/digits only, max 6.</p>
                  </div>
                  <div className="text-sm text-slate-600">
                    <div className="mb-1"><span className="text-slate-500">Preview next order number:</span></div>
                    <div className="font-mono text-lg tracking-wider bg-slate-50 border rounded px-3 py-2">
                      {(orderNumberPrefix || '').toUpperCase() || <span className="text-slate-400">(none)</span>}
                      <span className="text-slate-500">{String(new Date().getFullYear()).slice(-2)}001</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Existing orders keep their current number — this only applies to new orders. Typing either <span className="font-mono">SIS26001</span> or <span className="font-mono">26001</span> in the search bar will find the same order.
                    </p>
                  </div>
                  <Button
                    onClick={saveOrderNumberPrefix}
                    disabled={savingOrderPrefix || (orderNumberPrefix || '').toUpperCase() === (savedOrderNumberPrefix || '').toUpperCase()}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="order-number-prefix-save-btn"
                  >
                    {savingOrderPrefix ? 'Saving…' : 'Save Prefix'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Task Automation Settings */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Task Automation</CardTitle>
                      <CardDescription>Configure default assignees for automated tasks</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-6">
                  When tasks are automatically created (e.g., when artwork is approved), they will be assigned to the users you configure below.
                </p>
                
                <div className="space-y-4">
                  {/* Default Assignee */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Default Assignee</Label>
                      <p className="text-xs text-slate-500 mt-1">Fallback when no specific trigger is configured</p>
                    </div>
                    <select
                      value={taskAssignees.default || ''}
                      onChange={(e) => handleTaskAssigneeChange('default', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">No default assignee</option>
                      {companyUsers.map(u => (
                        <option key={u.id} value={u.email}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>

                  {/* Artwork Approved */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Artwork Approved</Label>
                      <p className="text-xs text-slate-500 mt-1">Assigned when customer approves artwork</p>
                    </div>
                    <select
                      value={taskAssignees['artwork-approved'] || ''}
                      onChange={(e) => handleTaskAssigneeChange('artwork-approved', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Use default assignee</option>
                      {companyUsers.map(u => (
                        <option key={u.id} value={u.email}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>

                  {/* Quote Sent */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Quote Sent</Label>
                      <p className="text-xs text-slate-500 mt-1">Assigned when a quote is sent to customer</p>
                    </div>
                    <select
                      value={taskAssignees['quote'] || ''}
                      onChange={(e) => handleTaskAssigneeChange('quote', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">Use default assignee</option>
                      {companyUsers.map(u => (
                        <option key={u.id} value={u.email}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={saveTaskAssignees} 
                    disabled={taskAssigneesLoading}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {taskAssigneesLoading ? 'Saving...' : 'Save Task Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Branch Management */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Branch Management</CardTitle>
                      <CardDescription>Manage company branches and locations</CardDescription>
                    </div>
                  </div>
                  <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={openCreateBranchDialog}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Branch
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{branchEditMode ? 'Edit Branch' : 'Add New Branch'}</DialogTitle>
                        <DialogDescription>
                          {branchEditMode ? 'Update branch details' : 'Add a new branch location'}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleBranchSubmit} className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="col-span-2 space-y-2">
                            <Label htmlFor="branch-name">Branch Name *</Label>
                            <Input
                              id="branch-name"
                              placeholder="e.g., Oxford Branch"
                              value={branchFormData.name}
                              onChange={(e) => setBranchFormData({ ...branchFormData, name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="branch-short-code">Short Code *</Label>
                            <Input
                              id="branch-short-code"
                              placeholder="e.g., OX"
                              value={branchFormData.short_code}
                              onChange={(e) => setBranchFormData({ ...branchFormData, short_code: e.target.value.toUpperCase().slice(0, 3) })}
                              maxLength={3}
                              className="uppercase font-mono"
                              required
                            />
                            <p className="text-xs text-slate-500">For order numbers</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="branch-address">Address</Label>
                          <Textarea
                            id="branch-address"
                            placeholder="Full address"
                            value={branchFormData.address}
                            onChange={(e) => setBranchFormData({ ...branchFormData, address: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="branch-phone">Phone</Label>
                          <Input
                            id="branch-phone"
                            placeholder="Phone number"
                            value={branchFormData.phone}
                            onChange={(e) => setBranchFormData({ ...branchFormData, phone: e.target.value })}
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button type="button" variant="outline" onClick={() => setBranchDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" disabled={!branchFormData.name || !branchFormData.short_code}>
                            {branchEditMode ? 'Update' : 'Create'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {branches.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No branches added yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {branches.map((branch) => (
                      <div key={branch.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{branch.name}</h4>
                              {branch.short_code && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-mono rounded">
                                  {branch.short_code}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{branch.address}</p>
                            {branch.phone && <p className="text-sm text-slate-500">{branch.phone}</p>}
                            {branch.short_code && (
                              <p className="text-xs text-slate-400 mt-1">Orders: {branch.short_code}26xxx</p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditBranchDialog(branch)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteBranch(branch.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CorelDRAW Upload Integration */}
            <Card className="border-0 shadow-xl" data-testid="coreldraw-settings-card">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-lg">
                    <KeyRound className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">CorelDRAW Upload</CardTitle>
                    <CardDescription>Let your designers send artwork proofs directly from CorelDRAW into the right order — no logging in required.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase">Company Slug</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={corelSettings?.company_slug || ''}
                        readOnly
                        className="font-mono text-sm"
                        data-testid="coreldraw-company-slug"
                      />
                      <Button type="button" size="sm" variant="outline" onClick={() => copyCorelValue(corelSettings?.company_slug, 'Company slug')} data-testid="copy-company-slug-btn">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 uppercase">API Key</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={
                          corelSettings?.coreldraw_api_key
                            ? (corelKeyVisible ? corelSettings.coreldraw_api_key : corelSettings.coreldraw_api_key.slice(0, 8) + '••••••••••••••••••••••••')
                            : ''
                        }
                        placeholder={corelSettings?.has_key ? '' : 'Not generated yet — click "Generate Key"'}
                        readOnly
                        className="font-mono text-sm"
                        data-testid="coreldraw-api-key"
                      />
                      {corelSettings?.has_key && (
                        <Button type="button" size="sm" variant="outline" onClick={() => setCorelKeyVisible(v => !v)} data-testid="toggle-key-visibility-btn">
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button type="button" size="sm" variant="outline" onClick={() => copyCorelValue(corelSettings?.coreldraw_api_key, 'API key')} disabled={!corelSettings?.coreldraw_api_key} data-testid="copy-api-key-btn">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <Button
                    type="button"
                    onClick={regenerateCorelKey}
                    disabled={corelLoading}
                    className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white"
                    data-testid="regenerate-coreldraw-key-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${corelLoading ? 'animate-spin' : ''}`} />
                    {corelSettings?.has_key ? 'Regenerate Key' : 'Generate Key'}
                  </Button>
                  {corelSettings?.has_key && (
                    <span className="text-xs text-slate-500">Regenerating immediately invalidates the previous key.</span>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> How designers use it</h4>
                  <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Open the proof in CorelDRAW, export as PNG or PDF.</li>
                    <li>The plugin POSTs to <code className="px-1 py-0.5 bg-white border rounded text-xs">/api/corel/upload-proof</code> with header <code className="px-1 py-0.5 bg-white border rounded text-xs">Authorization: Bearer &lt;API key&gt;</code>.</li>
                    <li>Body (multipart): <code className="px-1 py-0.5 bg-white border rounded text-xs">company_slug</code>, <code className="px-1 py-0.5 bg-white border rounded text-xs">order_number</code>, <code className="px-1 py-0.5 bg-white border rounded text-xs">file</code>.</li>
                    <li>The proof lands as a new artwork version on the matching order, with a timeline entry and the order marked as artwork-created.</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EMAIL TAB */}
          <TabsContent value="email" className="space-y-6">
            {/* Email Templates Section */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Email Templates</CardTitle>
                      <CardDescription>Customize the emails sent to your customers</CardDescription>
                    </div>
                  </div>
                  {!canCustomizeTemplates && user?.role !== 'super_admin' && (
                    <Badge variant="outline" className="border-amber-500 text-amber-600">
                      <Lock className="w-3 h-3 mr-1" />
                      White-Label Required
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {user?.role === 'super_admin' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                      <SettingsIcon className="w-4 h-4 inline mr-2" />
                      As Super Admin, manage system-wide default templates and your Set in Stone templates from the <strong>Platform</strong> tab.
                    </p>
                  </div>
                )}
                {!canCustomizeTemplates && user?.role !== 'super_admin' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <p className="text-amber-800 text-sm">
                      <AlertTriangle className="w-4 h-4 inline mr-2" />
                      Custom email templates are available with a white-label subscription. Upgrade to customize your email communications.
                    </p>
                  </div>
                )}
                
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${user?.role === 'super_admin' ? 'opacity-50 pointer-events-none' : ''}`}>
                  {emailTemplates.map((template) => (
                    <div 
                      key={template.template_type}
                      className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-slate-900">{getTemplateTypeLabel(template.template_type)}</h4>
                            {template.is_custom && (
                              <Badge className="bg-green-100 text-green-800 text-xs">Customized</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mb-2">{getTemplateTypeDescription(template.template_type)}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEmailTemplateEditor(template)}
                            disabled={!canCustomizeTemplates || user?.role === 'super_admin'}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PRODUCTS TAB */}
          <TabsContent value="products" className="space-y-6">
            {/* Sundries Management */}
            <Card className="border-0 shadow-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Sundries Management</CardTitle>
                  <CardDescription>Manage additional items and their prices</CardDescription>
                </div>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Sundry
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editMode ? 'Edit Sundry' : 'Add New Sundry'}</DialogTitle>
                    <DialogDescription>
                      {editMode ? 'Update sundry details' : 'Add a new sundry item with price'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Input
                        id="description"
                        placeholder="e.g., Flower vase, Additional inscription"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price">Price (£)</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="is_active" className="cursor-pointer">Active (visible in order form)</Label>
                    </div>
                    <Button type="submit" className="w-full">
                      {editMode ? 'Update Sundry' : 'Create Sundry'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-500 py-8">Loading sundries...</p>
            ) : sundries.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No sundries yet. Add your first one!</p>
            ) : (
              <div className="space-y-3">
                {sundries.map((sundry) => (
                  <div
                    key={sundry.id}
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      sundry.is_active ? 'bg-slate-50' : 'bg-slate-100 opacity-60'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-semibold text-slate-900">{sundry.description}</h3>
                        <Badge variant={sundry.is_active ? 'default' : 'secondary'}>
                          {sundry.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-lg font-bold text-blue-600 mt-1">£{sundry.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActive(sundry)}
                      >
                        {sundry.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(sundry)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(sundry.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

            {/* Lettering Rates - Show for memorial companies */}
            {hasMemorialsFeature && (
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Lettering Rates</CardTitle>
                      <CardDescription>Configure per-letter pricing for inscriptions</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(letteringRates).map(([key, value]) => (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg relative" data-testid={`lettering-rate-${key}`}>
                        <button
                          type="button"
                          onClick={() => removeLetteringType(key)}
                          className="absolute top-1.5 right-2 text-slate-400 hover:text-red-500 text-sm leading-none"
                          title="Remove lettering type"
                          data-testid={`remove-lettering-${key}`}
                        >
                          ×
                        </button>
                        <Label className="text-xs text-slate-500 capitalize pr-4 block">{key.replace(/_/g, ' ')}</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={value}
                            onChange={(e) => setLetteringRates({...letteringRates, [key]: parseFloat(e.target.value) || 0})}
                            className="h-8 text-sm"
                          />
                          <span className="text-xs text-slate-400 ml-1">/char</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-wrap items-center gap-3">
                    <div>
                      <Label className="text-xs text-slate-600">Free letters included (per inscription)</Label>
                      <p className="text-xs text-slate-400">Characters beyond this are charged at the per-character rate</p>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      value={freeLetters}
                      onChange={(e) => setFreeLetters(e.target.value)}
                      className="h-8 text-sm w-28"
                      data-testid="free-letters-input"
                    />
                  </div>
                  <div className="mt-4 p-3 border border-dashed border-slate-300 rounded-lg">
                    <Label className="text-xs text-slate-500">Add a new lettering type</Label>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <Input
                        placeholder="e.g. Enamel Infill"
                        value={newLetteringType.name}
                        onChange={(e) => setNewLetteringType({ ...newLetteringType, name: e.target.value })}
                        className="h-8 text-sm max-w-xs"
                        data-testid="new-lettering-name-input"
                      />
                      <div className="flex items-center">
                        <span className="text-slate-400 mr-1">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={newLetteringType.rate}
                          onChange={(e) => setNewLetteringType({ ...newLetteringType, rate: e.target.value })}
                          className="h-8 text-sm w-24"
                          data-testid="new-lettering-rate-input"
                        />
                        <span className="text-xs text-slate-400 ml-1">/char</span>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={addLetteringType} data-testid="add-lettering-type-btn">
                        + Add Type
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button onClick={saveLetteringRates} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-lettering-rates-btn">
                      Save Lettering Rates
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SUPPLIERS TAB */}
          <TabsContent value="suppliers" className="space-y-6">
            {/* Suppliers Management */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg">
                      <Truck className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Suppliers</CardTitle>
                      <CardDescription>Manage stone suppliers for purchase orders</CardDescription>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setSupplierEditMode(false);
                      setCurrentSupplier(null);
                      setSupplierFormData({ name: '', email: '', phone: '', address: '', contact_name: '', notes: '' });
                      setSupplierDialogOpen(true);
                    }}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Supplier
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {suppliers.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No suppliers added yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {suppliers.map((supplier) => (
                      <div key={supplier.id} className="p-4 bg-slate-50 rounded-lg border">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-slate-900">{supplier.name}</h4>
                            {supplier.contact_name && <p className="text-sm text-slate-600">Contact: {supplier.contact_name}</p>}
                            <p className="text-sm text-slate-500">{supplier.email}</p>
                            {supplier.phone && <p className="text-sm text-slate-500">{supplier.phone}</p>}
                          </div>
                          <div className="flex space-x-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setSupplierEditMode(true);
                              setCurrentSupplier(supplier);
                              setSupplierFormData(supplier);
                              setSupplierDialogOpen(true);
                            }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSupplier(supplier.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PO Templates */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-violet-600 to-purple-600 rounded-lg">
                      <Printer className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Quick Reorder Templates</CardTitle>
                      <CardDescription>Save templates for frequently ordered items</CardDescription>
                    </div>
                  </div>
                  <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        setTemplateEditMode(false);
                        setCurrentTemplate(null);
                        setTemplateFormData({ name: '', supplier_id: '', items: '', notes: '' });
                        setTemplateDialogOpen(true);
                      }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Template
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{templateEditMode ? 'Edit Template' : 'Create Reorder Template'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleTemplateSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Template Name</Label>
                          <Input
                            placeholder="e.g., Monthly Black Granite Order"
                            value={templateFormData.name}
                            onChange={(e) => setTemplateFormData({...templateFormData, name: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Supplier</Label>
                          <select
                            value={templateFormData.supplier_id}
                            onChange={(e) => setTemplateFormData({...templateFormData, supplier_id: e.target.value})}
                            className="w-full px-3 py-2 border rounded-md"
                            required
                          >
                            <option value="">Select supplier</option>
                            {suppliers.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Items</Label>
                          <Textarea
                            placeholder="List items to order..."
                            value={templateFormData.items}
                            onChange={(e) => setTemplateFormData({...templateFormData, items: e.target.value})}
                            rows={4}
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
                          <Button type="submit">{templateEditMode ? 'Update' : 'Create'}</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {poTemplates.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Printer className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No reorder templates yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {poTemplates.map((template) => (
                      <div key={template.id} className="p-4 bg-slate-50 rounded-lg border flex justify-between items-center">
                        <div>
                          <h4 className="font-medium">{template.name}</h4>
                          <p className="text-sm text-slate-500">
                            {suppliers.find(s => s.id === template.supplier_id)?.name || 'Unknown supplier'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleQuickOrder(template)}>
                            Quick Order
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MAINTENANCE TAB */}
          <TabsContent value="maintenance" className="space-y-6">
            {/* Maintenance Schedules */}
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Maintenance Schedules</CardTitle>
                      <CardDescription>Track equipment maintenance and servicing</CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => window.open(`${API}/maintenance-schedules/print-all?token=${token}`, '_blank')} data-testid="print-maintenance-schedule-btn">
                      <Printer className="w-4 h-4 mr-2" />
                      Print Schedule
                    </Button>
                    <Button onClick={() => {
                      setMaintenanceEditMode(false);
                      setCurrentMaintenance(null);
                      setMaintenanceFormData({ machine_name: '', description: '', frequency: 'monthly', checklist: [], next_due_date: '' });
                      setMaintenanceDialogOpen(true);
                    }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Schedule
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-6">
                  Keep track of regular maintenance for your equipment, machinery, and vehicles. Set up schedules to receive reminders when maintenance is due.
                </p>
                {maintenanceSchedules.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No maintenance schedules set up yet</p>
                    <p className="text-sm mt-1">Add your first schedule to start tracking equipment maintenance</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {maintenanceSchedules.map((schedule) => (
                      <div key={schedule.id} className={`p-4 rounded-lg border-2 ${isDue(schedule) ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-lg">{schedule.machine_name}</h4>
                              {isDue(schedule) && (
                                <Badge className="bg-red-600">Overdue</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{schedule.description}</p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Badge variant="outline" className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {getFrequencyLabel(schedule.frequency)}
                              </Badge>
                              {schedule.next_due_date && (
                                <Badge className={isDue(schedule) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                                  Due: {new Date(schedule.next_due_date).toLocaleDateString('en-GB')}
                                </Badge>
                              )}
                            </div>
                            {schedule.checklist && schedule.checklist.length > 0 && (
                              <div className="mt-3 text-xs text-slate-500">
                                {schedule.checklist.length} checklist item{schedule.checklist.length !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 ml-2">
                            <Button variant="ghost" size="sm" onClick={() => printJobSheet(schedule.id)} title="Print job sheet" data-testid={`jobsheet-${schedule.id}`}>
                              <Printer className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => {
                              setMaintenanceEditMode(true);
                              setCurrentMaintenance(schedule);
                              setMaintenanceFormData(schedule);
                              setMaintenanceDialogOpen(true);
                            }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteMaintenanceSchedule(schedule.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* WORKTOPS TAB */}
          {hasWorktopsFeature && (
            <TabsContent value="worktops" className="space-y-6">
              {/* Worktop Pricing Settings */}
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-lg">
                      <Wrench className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Worktop & Hearth Pricing</CardTitle>
                      <CardDescription>Configure pricing for worktop and hearth services</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Retail Multiplier */}
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <Label className="text-sm font-medium">Retail Price Multiplier</Label>
                    <p className="text-xs text-slate-500 mb-2">Applied to trade prices to calculate retail prices</p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={worktopSettings.retail_multiplier}
                        onChange={(e) => setWorktopSettings({...worktopSettings, retail_multiplier: parseFloat(e.target.value) || 1})}
                        className="w-32"
                      />
                      <span className="text-slate-500">x</span>
                    </div>
                  </div>

                  {/* Service Costs Grid */}
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">Service Costs</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Templating Cost</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.templating_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, templating_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Fitting Cost</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.fitting_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, fitting_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Custom Fabrication</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.custom_fabrication_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, custom_fabrication_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Delivery (per mile)</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.delivery_rate_per_mile || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, delivery_rate_per_mile: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sink Cutouts */}
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">Sink & Cutout Costs</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Undermount Sink</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.undermount_sink_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, undermount_sink_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Overmount Sink</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.overmount_sink_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, overmount_sink_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Hob Cutout</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.hob_cutout_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, hob_cutout_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Socket Cutout</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.socket_cutout_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, socket_cutout_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Pop-up Socket</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.popup_socket_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, popup_socket_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Tap Hole</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.tap_hole_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, tap_hole_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Custom Cutout (per m)</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.custom_cutout_cost_per_m || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, custom_cutout_cost_per_m: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Corner & Edge Options */}
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">Corner & Drainer Costs</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Radius Corner</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.radius_corner_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, radius_corner_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Large Rounded Corner</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.large_rounded_corner_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, large_rounded_corner_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Drainer Grooves</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.drainer_grooves_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, drainer_grooves_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Recessed Drainer</Label>
                        <div className="flex items-center mt-1">
                          <span className="text-slate-400 mr-1">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={worktopSettings.recessed_drainer_cost || 0}
                            onChange={(e) => setWorktopSettings({...worktopSettings, recessed_drainer_cost: parseFloat(e.target.value) || 0})}
                            className="h-8"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Edge Profiles */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Edge Profiles</h4>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Profile name"
                          value={newEdgeProfile.name}
                          onChange={(e) => setNewEdgeProfile({...newEdgeProfile, name: e.target.value})}
                          className="w-32 h-8"
                        />
                        <Input
                          type="number"
                          placeholder="Cost"
                          value={newEdgeProfile.cost}
                          onChange={(e) => setNewEdgeProfile({...newEdgeProfile, cost: e.target.value})}
                          className="w-20 h-8"
                        />
                        <Button size="sm" onClick={addEdgeProfile}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {Object.entries(worktopSettings.edge_profiles || {}).sort((a, b) => a[1] - b[1]).map(([name, cost]) => (
                        <div key={name} className="p-3 bg-slate-50 rounded-lg">
                          {editingEdge === name ? (
                            <div className="flex items-center gap-2">
                              <Input
                                defaultValue={name}
                                id={`edge-name-${name}`}
                                placeholder="Profile name"
                                className="h-8 flex-1"
                              />
                              <Input
                                type="number"
                                step="0.01"
                                defaultValue={cost}
                                id={`edge-cost-${name}`}
                                placeholder="£/m"
                                className="h-8 w-24"
                              />
                              <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700" onClick={() => {
                                const newName = document.getElementById(`edge-name-${name}`).value;
                                const newCost = document.getElementById(`edge-cost-${name}`).value;
                                updateEdgeProfile(name, newName, newCost);
                              }}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingEdge(null)}>Cancel</Button>
                            </div>
                          ) : (
                            <div className="flex justify-between items-center">
                              <div className="cursor-pointer flex-1" onClick={() => setEditingEdge(name)}>
                                <Label className="text-xs text-slate-500">{name}</Label>
                                <p className="font-medium">£{cost}/m</p>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setEditingEdge(name)} className="text-blue-600">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => removeEdgeProfile(name)} className="text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Mitred Edge Cost */}
                    <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium text-amber-800">Mitred Edge Cost</Label>
                          <p className="text-xs text-amber-600">Price per linear meter for mitred edges</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-amber-700">£</span>
                          <Input
                            type="number"
                            value={worktopSettings.mitred_edge_cost || 45}
                            onChange={(e) => setWorktopSettings({ ...worktopSettings, mitred_edge_cost: parseFloat(e.target.value) || 0 })}
                            className="w-24 h-9"
                            step="0.01"
                          />
                          <span className="text-sm text-amber-700">/m</span>
                        </div>
                      </div>
                    </div>

                    {/* Face Polishing Cost - Separate section */}
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-blue-800 font-medium">Face Polishing Cost</Label>
                          <p className="text-xs text-blue-600">Price per square meter for face polishing</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-blue-700">£</span>
                          <Input
                            type="number"
                            value={worktopSettings.face_polish_cost || 0}
                            onChange={(e) => setWorktopSettings({ ...worktopSettings, face_polish_cost: parseFloat(e.target.value) || 0 })}
                            className="w-24 h-9"
                            step="0.01"
                          />
                          <span className="text-sm text-blue-700">/m²</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Auto-Archive Settings */}
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">Auto-Archive Settings</h4>
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium text-amber-900">Quote Archive Threshold</Label>
                          <p className="text-xs text-amber-700 mt-1">Quotes older than this will be flagged for archiving when you log in</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="7"
                            max="365"
                            value={worktopSettings.quote_archive_days || 30}
                            onChange={(e) => setWorktopSettings({...worktopSettings, quote_archive_days: parseInt(e.target.value) || 30})}
                            className="w-20 h-9"
                            data-testid="quote-archive-days-input"
                          />
                          <span className="text-sm text-amber-800">days</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={saveWorktopSettings} className="bg-teal-600 hover:bg-teal-700">
                      Save Worktop Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Email Template Editor Dialog with Live Preview */}
        <Dialog open={emailTemplateDialogOpen} onOpenChange={setEmailTemplateDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                {currentEmailTemplate ? getTemplateTypeLabel(currentEmailTemplate.template_type) : 'Email Template'}
              </DialogTitle>
              <DialogDescription>
                Edit your template on the left, see a live preview on the right. Variables like {'{client_name}'} will be replaced with actual data.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-6 py-4 h-[65vh]">
              {/* Left side - Editor */}
              <div className="space-y-4 overflow-y-auto pr-4">
                {/* Logo Options */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <Label className="font-medium text-blue-800 mb-3 block flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Email Logo
                  </Label>
                  <div className="space-y-3">
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="logoOption"
                          value="company"
                          checked={emailLogoOption === 'company'}
                          onChange={(e) => setEmailLogoOption(e.target.value)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">Use company logo from settings</span>
                        {(companyBranding.logo_url || companyBranding.logo_data) && (
                          <img 
                            src={companyBranding.logo_url || companyBranding.logo_data} 
                            alt="Current" 
                            className="h-6 w-auto ml-2 border rounded"
                          />
                        )}
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="logoOption"
                          value="custom"
                          checked={emailLogoOption === 'custom'}
                          onChange={(e) => setEmailLogoOption(e.target.value)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">Upload a new logo</span>
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="logoOption"
                          value="none"
                          checked={emailLogoOption === 'none'}
                          onChange={(e) => setEmailLogoOption(e.target.value)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">No logo</span>
                      </label>
                    </div>
                    
                    {/* Upload area - shown when custom is selected */}
                    {emailLogoOption === 'custom' && (
                      <div className="mt-3 p-3 bg-white rounded-lg border-2 border-dashed border-blue-200">
                        <input
                          type="file"
                          ref={logoFileInputRef}
                          onChange={handleLogoFileSelect}
                          accept="image/*"
                          className="hidden"
                        />
                        
                        {customEmailLogoPreview ? (
                          <div className="flex items-center gap-3">
                            <img 
                              src={customEmailLogoPreview} 
                              alt="Preview" 
                              className="h-12 w-auto object-contain border rounded"
                            />
                            <div className="flex-1">
                              <p className="text-sm text-slate-600">Logo selected</p>
                              <div className="flex gap-2 mt-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={handleLogoUpload}
                                  disabled={uploadingLogo}
                                  className="bg-blue-600 hover:bg-blue-700 h-7 text-xs"
                                >
                                  {uploadingLogo ? 'Saving...' : 'Save as Company Logo'}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setCustomEmailLogo('');
                                    setCustomEmailLogoPreview('');
                                  }}
                                  className="h-7 text-xs"
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Clear
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="text-center cursor-pointer py-2"
                            onClick={() => logoFileInputRef.current?.click()}
                          >
                            <Upload className="w-8 h-8 mx-auto text-blue-400 mb-2" />
                            <p className="text-sm text-slate-600">Click to select an image</p>
                            <p className="text-xs text-slate-400 mt-1">PNG, JPG up to 2MB</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Available Variables */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">Click to copy variable:</p>
                  <div className="flex flex-wrap gap-1">
                    {(templateVariables[emailTemplateForm.template_type] || []).map(variable => (
                      <Badge 
                        key={variable} 
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-blue-100 hover:border-blue-300"
                        onClick={() => {
                          navigator.clipboard.writeText(`{${variable}}`);
                          toast.success(`Copied {${variable}}`);
                        }}
                      >
                        {'{' + variable + '}'}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-medium">Email Subject</Label>
                  <Input
                    value={emailTemplateForm.subject}
                    onChange={(e) => setEmailTemplateForm({...emailTemplateForm, subject: e.target.value})}
                    placeholder="e.g., Your Quote from {company_name}"
                    className="font-medium"
                  />
                </div>
                
                <div className="space-y-2 flex-1">
                  <Label className="font-medium">Email Body</Label>
                  <Textarea
                    value={emailTemplateForm.body}
                    onChange={(e) => setEmailTemplateForm({...emailTemplateForm, body: e.target.value})}
                    placeholder="Write your email content here..."
                    rows={12}
                    className="font-mono text-sm resize-none"
                  />
                </div>
              </div>
              
              {/* Right side - Live Preview */}
              <div className="border rounded-lg overflow-hidden bg-slate-100 flex flex-col">
                <div className="bg-slate-200 px-4 py-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Live Preview</span>
                  <Badge variant="outline" className="text-xs">Sample Data</Badge>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {/* Email Preview Container */}
                  <div className="bg-white rounded-lg shadow-sm border max-w-xl mx-auto">
                    {/* Email Header with Logo */}
                    <div className="p-6 border-b bg-gradient-to-r from-slate-50 to-white">
                      <div className="flex items-center gap-4">
                        {getPreviewLogoUrl() ? (
                          <img 
                            src={getPreviewLogoUrl()} 
                            alt="Company Logo" 
                            className="h-16 w-auto object-contain"
                          />
                        ) : emailLogoOption !== 'none' ? (
                          <div className="h-16 w-16 bg-slate-200 rounded-lg flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-slate-400" />
                          </div>
                        ) : null}
                        <div>
                          <h2 className="text-xl font-semibold text-slate-900">
                            {companyBranding.company_name || 'Your Company Name'}
                          </h2>
                          {companyBranding.tagline && (
                            <p className="text-sm text-slate-500">{companyBranding.tagline}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Email Subject Preview */}
                    <div className="px-6 py-3 bg-slate-50 border-b">
                      <p className="text-xs text-slate-500 mb-1">Subject:</p>
                      <p className="font-semibold text-slate-900">
                        {generateEmailPreview().subject || 'Email Subject'}
                      </p>
                    </div>
                    
                    {/* Email Body Preview */}
                    <div className="p-6">
                      <div 
                        className="text-slate-700 leading-relaxed"
                        style={{ fontFamily: 'Georgia, serif', fontSize: '15px' }}
                        dangerouslySetInnerHTML={{ __html: generateEmailPreview().body || '<p class="text-slate-400">Email body will appear here...</p>' }}
                      />
                    </div>
                    
                    {/* Email Footer */}
                    <div className="px-6 py-4 bg-slate-50 border-t text-center">
                      <div className="text-sm text-slate-600 space-y-1">
                        <p className="font-medium">{companyBranding.company_name || 'Your Company Name'}</p>
                        {companyBranding.company_address && (
                          <p className="text-slate-500">{companyBranding.company_address}</p>
                        )}
                        <div className="flex items-center justify-center gap-4 text-slate-500">
                          {companyBranding.company_phone && (
                            <span>{companyBranding.company_phone}</span>
                          )}
                          {companyBranding.company_email && (
                            <span>{companyBranding.company_email}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="border-t pt-4">
              <div className="flex items-center gap-2 mr-auto text-xs text-slate-500">
                <AlertTriangle className="w-4 h-4" />
                Company logo and details are pulled from your company settings
              </div>
              <Button variant="outline" onClick={() => setEmailTemplateDialogOpen(false)}>
                Cancel
              </Button>
              {(canCustomizeTemplates || user?.role === 'super_admin') && (
                <Button onClick={saveEmailTemplate} className="bg-blue-600 hover:bg-blue-700">
                  Save Template
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Supplier Dialog */}
        <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{supplierEditMode ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSupplierSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={supplierFormData.name}
                    onChange={(e) => setSupplierFormData({...supplierFormData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={supplierFormData.contact_name}
                    onChange={(e) => setSupplierFormData({...supplierFormData, contact_name: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={supplierFormData.email}
                    onChange={(e) => setSupplierFormData({...supplierFormData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={supplierFormData.phone}
                    onChange={(e) => setSupplierFormData({...supplierFormData, phone: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={supplierFormData.address}
                  onChange={(e) => setSupplierFormData({...supplierFormData, address: e.target.value})}
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setSupplierDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{supplierEditMode ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Maintenance Schedule Dialog */}
        <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{maintenanceEditMode ? 'Edit Maintenance Schedule' : 'Add Maintenance Schedule'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleMaintenanceSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Machine/Equipment Name</Label>
                <Input
                  value={maintenanceFormData.machine_name}
                  onChange={(e) => setMaintenanceFormData({...maintenanceFormData, machine_name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={maintenanceFormData.description}
                  onChange={(e) => setMaintenanceFormData({...maintenanceFormData, description: e.target.value})}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <select
                    value={maintenanceFormData.frequency}
                    onChange={(e) => setMaintenanceFormData({...maintenanceFormData, frequency: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Next Due Date</Label>
                  <Input
                    type="date"
                    value={maintenanceFormData.next_due_date}
                    onChange={(e) => setMaintenanceFormData({...maintenanceFormData, next_due_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setMaintenanceDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{maintenanceEditMode ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default Settings;
